import React from 'react';
import {
  CalendarIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const Press: React.FC = () => {
  const pressReleases = [
    {
      id: '1',
      title: 'Maasai SightTrails Launches New Conservation Program',
      date: '2024-01-15',
      summary: 'We announce a new wildlife conservation initiative in partnership with local communities.',
      image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'Conservation'
    },
    {
      id: '2',
      title: 'Award-Winning Safari Experience Recognized by Tourism Board',
      date: '2024-01-10',
      summary: 'Our authentic Maasai cultural experiences receive national recognition for excellence.',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb2d15?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'Awards'
    },
    {
      id: '3',
      title: 'New Photography Safari Program Launched',
      date: '2024-01-05',
      summary: 'Professional wildlife photography workshops now available for photography enthusiasts.',
      image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'New Program'
    }
  ];

  const mediaCoverage = [
    {
      id: '1',
      title: 'National Geographic Features Maasai SightTrails',
      source: 'National Geographic',
      date: '2023-12-20',
      description: 'Our conservation efforts and authentic cultural experiences featured in National Geographic.',
      link: '#',
      image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      id: '2',
      title: 'CNN Travel Highlights Sustainable Tourism',
      source: 'CNN Travel',
      date: '2023-12-15',
      description: 'Our sustainable tourism practices and community engagement featured on CNN Travel.',
      link: '#',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb2d15?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      id: '3',
      title: 'BBC Wildlife Documentary Partnership',
      source: 'BBC Wildlife',
      date: '2023-12-10',
      description: 'Collaboration with BBC Wildlife for documentary on East African wildlife conservation.',
      link: '#',
      image: 'https://images.unsplash.com/photo-1551698618-1dfe5d97d256?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ];

  const awards = [
    {
      year: '2023',
      title: 'Best Cultural Tourism Experience',
      organization: 'Kenya Tourism Board',
      description: 'Recognition for authentic Maasai cultural experiences and community engagement.'
    },
    {
      year: '2023',
      title: 'Sustainable Tourism Award',
      organization: 'East Africa Tourism Association',
      description: 'Award for outstanding commitment to sustainable tourism practices.'
    },
    {
      year: '2022',
      title: 'Wildlife Conservation Excellence',
      organization: 'African Wildlife Foundation',
      description: 'Recognition for wildlife conservation efforts and community education programs.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <div className="bg-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Press & Media</h1>
          <p className="text-xl opacity-90 max-w-3xl mx-auto">
            Stay updated with our latest news, press releases, and media coverage of Maasai SightTrails Safaris.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Press Banner */}
        <div className="relative rounded-lg overflow-hidden shadow-sm mb-12">
          <img
            src="https://images.unsplash.com/photo-1504711434969-e33886168f5c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt="Press conference and media coverage"
            className="w-full h-64 object-cover"
          />
          <div className="absolute inset-0 bg-primary-900/60 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-3xl font-bold mb-2">In the News</h2>
              <p className="text-xl">Recognized for excellence in safari tourism</p>
            </div>
          </div>
        </div>

        {/* Press Releases */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8">Latest Press Releases</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pressReleases.map((release) => (
              <div key={release.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-neutral-200 hover:shadow-md transition-shadow duration-200">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={release.image}
                    alt={release.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-primary-800 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {release.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-neutral-500 mb-2">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {new Date(release.date).toLocaleDateString()}
                  </div>
                  <h3 className="text-lg font-bold text-primary-900 mb-3 line-clamp-2">
                    {release.title}
                  </h3>
                  <p className="text-neutral-600 mb-4 line-clamp-3">
                    {release.summary}
                  </p>
                  <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                    Read More →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Media Coverage */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8">Media Coverage</h2>
          <div className="space-y-6">
            {mediaCoverage.map((coverage) => (
              <div key={coverage.id} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200 hover:shadow-md transition-shadow duration-200">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-48 flex-shrink-0">
                    <img
                      src={coverage.image}
                      alt={coverage.title}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-2">
                      <span className="text-sm text-neutral-500">
                        {new Date(coverage.date).toLocaleDateString()}
                      </span>
                      <span className="text-sm font-medium text-primary-600">
                        {coverage.source}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-primary-900 mb-3">
                      {coverage.title}
                    </h3>
                    <p className="text-neutral-600 mb-4">
                      {coverage.description}
                    </p>
                    <a
                      href={coverage.link}
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Read Article
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Awards */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-primary-900 mb-8">Awards & Recognition</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {awards.map((award, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200 text-center">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <StarIcon className="h-8 w-8 text-accent-600" />
                </div>
                <div className="text-2xl font-bold text-accent-600 mb-2">{award.year}</div>
                <h3 className="text-lg font-bold text-primary-900 mb-2">{award.title}</h3>
                <p className="text-primary-600 font-medium mb-3">{award.organization}</p>
                <p className="text-neutral-600 text-sm">{award.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Media Kit */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-neutral-200">
          <h2 className="text-2xl font-bold text-primary-900 mb-6 text-center">Media Kit</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-primary-900 mb-4">Press Resources</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="text-neutral-700">Company Fact Sheet</span>
                </div>
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="text-neutral-700">High-Resolution Images</span>
                </div>
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="text-neutral-700">Logo Assets</span>
                </div>
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-3" />
                  <span className="text-neutral-700">Press Contact Information</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-primary-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-primary-900">Press Contact:</span>
                  <p className="text-neutral-700">Sarah Mwangi</p>
                  <p className="text-neutral-700">Director of Communications</p>
                </div>
                <div>
                  <span className="font-medium text-primary-900">Email:</span>
                  <p className="text-neutral-700">sighttrailsafaris@gmail.com</p>
                </div>
                <div>
                  <span className="font-medium text-primary-900">Phone:</span>
                  <p className="text-neutral-700">+254 708 560 665</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <button className="bg-primary-800 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200">
              Download Media Kit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Press;
